import firebase from './firebase';
import { markDeliberateSignOut } from './authGuard';

// One place where a successful login turns into app session state, shared by both
// sign-in paths on LoginPage: email/password (signInWithEmailAndPassword) and PIN
// (signInWithCustomToken). Both end up in the identical auth state against the same
// uid and users/{uid} doc — the storage keys below ARE the app's login gate
// (ProtectedRoute reads `authed`; StateManager reads whiteList/userType), so the two
// paths must write them the same way or permissions silently differ by how you
// logged in.

const SESSION_KEYS = [
  'authed', 'userID', 'whiteList', 'userType', 'userName',
  'pinSession', 'pinSessionMinutes', 'pinLastActivity', 'pinUserName',
  // Cleared too, so a timed-out PIN session on a shop tablet doesn't leave the next
  // person identified as the previous mechanic or vendor.
  'mechanicId', 'mechanicName', 'vendor',
];

// Shared-device PIN sessions idle out. Firebase itself can't do this for us:
// firebase.js forces browserLocalPersistence globally, so a PIN session survives a
// tab close no matter which Storage we write to — the timeout has to be explicit.
//
// One day, not one hour. The 60-minute default signed porters and mechanics out
// across a lunch break or a long job in the shop, and they had to re-enter a PIN
// several times a shift. An admin can still set a shorter (or zero = never) value
// per user on the User Management page; this is only what a blank field means.
const DEFAULT_PIN_SESSION_MINUTES = 24 * 60;
const IDLE_CHECK_INTERVAL_MS = 30 * 1000;
// pointerdown/click cover taps that never produce a mousedown on touch devices.
// visibilitychange is handled separately below; it fires at the document.
const ACTIVITY_EVENTS = ['mousedown', 'pointerdown', 'click', 'keydown', 'touchstart', 'scroll'];
// scroll alone fires dozens of times a second, and every write here is a localStorage
// write other tabs receive as a `storage` event. One write per interval is enough
// precision for a timeout measured in hours.
const ACTIVITY_WRITE_INTERVAL_MS = 30 * 1000;

export const establishSession = ({ uid, user = {}, remember = true, viaPin = false }) => {
  const storage = remember ? localStorage : sessionStorage;
  const { whiteList = [], userType, firstName = '', lastName = '', vendor = '' } = user;

  storage.setItem('authed', true);
  storage.setItem('userID', uid);
  storage.setItem('whiteList', JSON.stringify(whiteList || []));
  storage.setItem('userType', userType || 'sales');

  const userName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (userName) storage.setItem('userName', userName);

  // A mechanic's uid IS their `mechanics` doc id, so a mechanic login already
  // identifies which mechanic this is. Written here, before the post-login reload,
  // so StateManager picks it up in its constructor — MechanicOrdersPage reads
  // StateManager.mechanicId during its first render and would otherwise show
  // "No mechanic ID found" while waiting for the users snapshot listener.
  if (userType === 'mechanic') {
    localStorage.setItem('mechanicId', uid);
    sessionStorage.setItem('mechanicId', uid);
    if (userName) {
      localStorage.setItem('mechanicName', userName);
      sessionStorage.setItem('mechanicName', userName);
    }
  }

  // Same reason as mechanicId: VendorDashboard resolves its vendor during the first
  // render, so this has to be readable before the users snapshot listener arrives.
  if (vendor) {
    localStorage.setItem('vendor', vendor);
    sessionStorage.setItem('vendor', vendor);
  } else {
    localStorage.removeItem('vendor');
    sessionStorage.removeItem('vendor');
  }

  if (viaPin) {
    // Marked in localStorage (not `storage`) so the idle watcher finds it from any
    // tab regardless of the remember-me choice.
    localStorage.setItem('pinSession', 'true');
    localStorage.setItem('pinLastActivity', String(Date.now()));
    const minutes = user.pinSessionMinutes;
    localStorage.setItem(
      'pinSessionMinutes',
      String(minutes === null || minutes === undefined ? DEFAULT_PIN_SESSION_MINUTES : minutes),
    );
    // Remembered so the PIN pad can pre-fill the name next time — the whole point
    // being that a returning porter only types 6 digits.
    if (userName) localStorage.setItem('pinUserName', JSON.stringify({ uid, name: userName }));
  } else {
    localStorage.removeItem('pinSession');
  }
};

export const clearSession = () => {
  SESSION_KEYS.forEach((key) => {
    // pinUserName deliberately survives — it's a convenience, not a credential.
    if (key === 'pinUserName') return;
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

// The one way to sign out on purpose. Flags the intent first (see authGuard) so every
// tab's auth listener skips its grace period, then tears the session down.
export const signOutDeliberately = async () => {
  markDeliberateSignOut();
  try {
    await firebase.auth().signOut();
  } catch (e) {
    // Local operation in practice; clear our own keys regardless.
  }
  clearSession();
};

// Where to send someone after they sign in.
//
// A load link (/load/<id>) sent to an outside carrier hits ProtectedRoute before they
// have a session, and that redirect used to drop the destination — they logged in and
// landed on the home screen, which for a driver has nothing on it. The attempted URL is
// stashed here instead, and consumed by the login page on success.
//
// It goes through storage rather than router state because login finishes with a full
// page load, which discards in-memory history state.
const REDIRECT_KEY = 'postLoginRedirect';
const REDIRECT_MAX_AGE_MS = 60 * 60 * 1000;

export const savePostLoginRedirect = (url) => {
  // Never bounce back to the login screen itself. The most recent attempt wins: if they
  // open one load link and then another before signing in, they want the second.
  if (!url || url === '/') return;
  try {
    sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ url, at: Date.now() }));
  } catch (e) {
    // Not being able to remember the destination is survivable; login still works.
  }
};

// Read and clear. Returns null if absent or stale, so a link opened yesterday can't
// silently redirect today's sign-in somewhere unexpected.
export const takePostLoginRedirect = () => {
  try {
    const raw = sessionStorage.getItem(REDIRECT_KEY);
    sessionStorage.removeItem(REDIRECT_KEY);
    if (!raw) return null;
    const { url, at } = JSON.parse(raw);
    if (!url || !at || Date.now() - at > REDIRECT_MAX_AGE_MS) return null;
    return url;
  } catch (e) {
    return null;
  }
};

export const getRememberedPinUser = () => {
  try {
    const raw = localStorage.getItem('pinUserName');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const forgetRememberedPinUser = () => localStorage.removeItem('pinUserName');

// Idle timeout for PIN sessions. Started once from App.js; a no-op for
// email/password sessions and for users an admin set to never time out (0).
export const startPinIdleWatch = () => {
  let interval = null;
  let lastWrite = 0;

  const markActivity = () => {
    if (localStorage.getItem('pinSession') !== 'true') return;
    const now = Date.now();
    if (now - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
    lastWrite = now;
    localStorage.setItem('pinLastActivity', String(now));
  };

  const check = async () => {
    if (localStorage.getItem('pinSession') !== 'true') return;

    const minutes = Number(localStorage.getItem('pinSessionMinutes'));
    if (!minutes || Number.isNaN(minutes)) return; // 0 / blank => never time out

    const last = Number(localStorage.getItem('pinLastActivity')) || Date.now();
    if (Date.now() - last < minutes * 60 * 1000) return;

    // signOut BEFORE clearing storage, and flag the intent first: firebase.js's
    // onAuthStateChanged re-adds `authed` whenever Firebase still holds a session, and
    // its grace period would otherwise treat this timeout as a blip and undo it.
    await signOutDeliberately();
    window.location.href = '/';
  };

  // Re-check on wake rather than counting the wake itself as activity. A frozen mobile
  // tab stops running the interval, so a tablet woken after the window has passed needs
  // an immediate verdict — and treating the wake as activity would extend a session
  // that had already gone stale.
  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };

  ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, markActivity, { passive: true }));
  document.addEventListener('visibilitychange', onVisible);
  interval = setInterval(check, IDLE_CHECK_INTERVAL_MS);

  return () => {
    ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, markActivity));
    document.removeEventListener('visibilitychange', onVisible);
    if (interval) clearInterval(interval);
  };
};
